// src-lit/components/product/kd-product-donation-picker.ts
import { customElement } from 'lit/decorators.js';
import { SELECTORS } from '../../constants/selectors';
import { KdVariantSyncedElement } from './kd-variant-synced-element';

interface DonationVariantEntry {
  /** バリアント単位の寄付金額（円）。パントリーは 0 で固定（カート総額 × 1% は別 issue） */
  donationAmount: number;
}

/**
 * @element kd-product-donation-picker
 * @description 支援金額の hidden input と表示 span をバリアント選択に応じて更新する。
 *   per-variant の `donationAmount` JSON を block 側で埋め込み、Lit は VARIANT_SELECTED 受信時に
 *   input.value と span.textContent を差し替えるだけ。HTML 生成は Liquid 側で完結（二重化禁止）。
 *
 *   パントリー商品は block 側で variant_map を出さないため variantMap が空 → onVariantSelected は
 *   そもそも発火しない（KdVariantSyncedElement の標準挙動で no-op になる）。
 */
@customElement('kd-product-donation-picker')
export class KdProductDonationPicker extends KdVariantSyncedElement<DonationVariantEntry> {
  protected readonly variantMapSelector = SELECTORS.PRODUCT_DONATION_PICKER.VARIANT_MAP;

  protected onVariantSelected(entry: DonationVariantEntry): void {
    const input = this.querySelector<HTMLInputElement>(
      SELECTORS.CART_ADD_FORM.DONATION_AMOUNT_INPUT,
    );
    if (input) input.value = String(entry.donationAmount);

    // SSR `donation_amount | times: 100 | money_without_currency` と等価（JPY shop の money_without_currency
    // は内部 cents を ÷100 して円表記する）。donationAmount は円単位なのでそのまま桁区切りで出す。
    const display = this.querySelector<HTMLElement>(
      SELECTORS.PRODUCT_DONATION_PICKER.AMOUNT_DISPLAY,
    );
    if (display) display.textContent = entry.donationAmount.toLocaleString('ja-JP');

    // donation_amount == 0 のバリアントは支援団体選択も意味が無いためセクションごと隠す（SSR 側で初期 hidden 属性付与）。
    // 要素は常に DOM にあるため、ゼロ↔非ゼロの双方向切替で見た目が実態と乖離しない。
    const section = this.querySelector<HTMLElement>(
      SELECTORS.PRODUCT_DONATION_PICKER.SECTION,
    );
    if (section) section.hidden = entry.donationAmount === 0;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-product-donation-picker': KdProductDonationPicker;
  }
}
